import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import NexovernIcon from '@/assets/nexovern-initial.png';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Activity,
  Bot,
  Cpu,
  Flame,
  FolderTree,
  LayoutDashboard,
  LogOut,
  Menu,
  Monitor,
  PanelLeftClose,
  PanelLeftOpen,
  Plug,
  Puzzle,
  ScrollText,
  Search,
  Settings as SettingsIcon,
  Shield,
  ShieldAlert,
  Sparkles,
  UserCog,
  X,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/components/ui';
import { useAuthStore } from '@/store/auth.store';
import { useRealtimeDetections } from '@/hooks/useRealtimeDetections';
import { NotificationBell } from '@/components/layout/NotificationBell';
import { CommandPalette } from '@/components/layout/CommandPalette';
import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { useCommandPaletteStore } from '@/store/commandPalette.store';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  /** [action, subject] — gate this item behind a permission instead of a hardcoded role list. */
  permission?: [string, string];
  /** Small count pill shown beside the label (expanded sidebar only). */
  badge?: number;
}

const NAV_GROUPS: { title: string; items: NavItem[] }[] = [
  {
    title: '',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard className="h-3.5 w-3.5" /> },
    ],
  },
  {
    title: 'Inventory',
    items: [
      { to: '/endpoints', label: 'Endpoints', icon: <Monitor className="h-3.5 w-3.5" /> },
      { to: '/agents',    label: 'Agents',    icon: <Bot className="h-3.5 w-3.5" /> },
      { to: '/mcps',      label: 'MCPs',      icon: <Cpu className="h-3.5 w-3.5" /> },
      { to: '/skills',    label: 'Skills',    icon: <Sparkles className="h-3.5 w-3.5" /> },
      { to: '/plugins',   label: 'Plugins',   icon: <Puzzle className="h-3.5 w-3.5" /> },
    ],
  },
  {
    title: 'Observability',
    items: [
      { to: '/ai-sessions', label: 'AI Sessions', icon: <Activity className="h-3.5 w-3.5" /> },
      { to: '/threats',     label: 'Threats',     icon: <Flame className="h-3.5 w-3.5" /> },
    ],
  },
  {
    title: 'Governance',
    items: [
      { to: '/owasp-top10', label: 'OWASP & MITRE Risks', icon: <ShieldAlert className="h-3.5 w-3.5" /> },
      { to: '/governance/classes', label: 'Classes', icon: <FolderTree className="h-3.5 w-3.5" />, permission: ['read', 'AgentClass'] },
      { to: '/audit-log',   label: 'Forensic Logs', icon: <ScrollText className="h-3.5 w-3.5" />, permission: ['read', 'AuditLog'] },
      { to: '/policies',    label: 'Policies',     icon: <Shield className="h-3.5 w-3.5" />, permission: ['read', 'Policy'] },
      { to: '/mcp-console', label: 'MCP Console',  icon: <Plug className="h-3.5 w-3.5" />,  permission: ['read', 'McpConsole'] },
    ],
  },
  {
    title: '',
    items: [
      { to: '/settings', label: 'Settings', icon: <SettingsIcon className="h-3.5 w-3.5" /> },
    ],
  },
];

export interface AppLayoutProps {
  children: ReactNode;
}

// Routes that need a full-bleed canvas layout (no padding wrapper, overflow-hidden)
const CANVAS_ROUTES: string[] = [];

export function AppLayout({ children }: AppLayoutProps) {
  const { user, logout, hasPermission } = useAuthStore();
  const badgeFor = (item: NavItem): number | undefined => item.badge;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isCanvas = CANVAS_ROUTES.some((r) => pathname.startsWith(r));
  const { toggle: togglePalette } = useCommandPaletteStore();
  // Desktop: collapsed vs expanded
  const [sidebarOpen, setSidebarOpen] = useState(true);
  // Mobile: overlay open vs closed
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const displayName = user?.name ?? user?.email ?? 'Analyst';
  const mainRef = useRef<HTMLElement>(null);
  useRealtimeDetections();

  // <main> is the single scroll container shared by every page, so its scrollTop would otherwise
  // carry over between routes. Reset it whenever the page (path) changes.
  useLayoutEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!userMenuRef.current?.contains(e.target as Node)) setUserMenuOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [userMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const handleAccountSettings = () => {
    setUserMenuOpen(false);
    navigate('/settings?tab=account');
  };

  // Shared nav content rendered inside both mobile overlay and desktop sidebar
  const renderNavContent = (collapsed: boolean, isMobileOverlay: boolean) => (
    <>
      {/* Branding and toggle row — always rendered at full size; never shrinks or animates with collapse.
          Overflows past the collapsed rail's edge on purpose (see aside's `relative z-40` + `overflow-visible`). */}
      <div className="flex items-center justify-between gap-2 h-12 w-52 shrink-0 px-3 border-b border-border-subtle bg-surface-0">
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-accent/15 text-accent flex-shrink-0">
          <img src={NexovernIcon} alt="Nexovern Logo" className="h-6 w-6" />
        </div>
        <span className="flex-shrink-0 whitespace-nowrap text-sm font-semibold">Nexovern</span>

        {/* Desktop collapse toggle — hidden on mobile overlay */}
        {!isMobileOverlay && (
          <button
            onClick={() => setSidebarOpen((o) => !o)}
            className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-sm text-text-secondary hover:bg-surface-2 hover:text-text-primary transition-colors flex-shrink-0"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
        )}

        {/* Mobile close button — only in mobile overlay */}
        {isMobileOverlay && (
          <button
            onClick={() => setMobileSidebarOpen(false)}
            className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-sm text-text-secondary hover:bg-surface-2 hover:text-text-primary transition-colors flex-shrink-0"
            aria-label="Close menu"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className={cn(
        'flex-1 space-y-5 overflow-y-auto',
        collapsed ? 'px-1.5 py-3' : 'px-2 py-3',
      )}>
        {NAV_GROUPS.map((group) => {
          const visibleItems = group.items.filter(
            (item) => !item.permission || hasPermission(item.permission[0], item.permission[1]),
          );
          if (visibleItems.length === 0) return null;
          return (
            <div key={group.title || group.items[0]?.to}>
              {!collapsed && group.title && (
                <div className="mb-1.5 px-2 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
                  {group.title}
                </div>
              )}
              <ul className={cn('space-y-0.5', collapsed && 'space-y-1')}>
                {visibleItems.map((item) => (
                  <li key={item.to} title={collapsed ? item.label : undefined}>
                    <NavLink
                      to={item.to}
                      onClick={isMobileOverlay ? () => setMobileSidebarOpen(false) : undefined}
                      className={({ isActive }) =>
                        cn(
                          'flex w-full items-center gap-2.5 rounded-sm transition-colors duration-[var(--duration-fast)] group relative',
                          collapsed ? 'px-2.5 py-2.5 justify-center' : 'px-2 py-1.5',
                          isActive
                            ? 'bg-accent/10 text-accent'
                            : 'text-text-secondary hover:bg-surface-1 hover:text-text-primary',
                        )
                      }
                    >
                      <span className="inline-flex shrink-0">{item.icon}</span>
                      {!collapsed && <span className="text-xs font-medium">{item.label}</span>}
                      {!collapsed && badgeFor(item) !== undefined && (
                        <span className="ml-auto rounded-full bg-state-warning/15 px-1.5 py-px text-[10px] font-semibold tabular-nums text-state-warning">
                          {badgeFor(item)}
                        </span>
                      )}
                      {collapsed && (
                        <div className="absolute left-full ml-2 px-2 py-1.5 bg-surface-2 text-text-primary text-xs rounded-sm whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 z-40">
                          {item.label}
                        </div>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden flex-row bg-surface-base text-text-primary">

      {/* ── Mobile sidebar overlay (< md) ─────────────────────────────────── */}
      {mobileSidebarOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={() => setMobileSidebarOpen(false)}
            aria-hidden
          />
          {/* Sidebar panel */}
          <aside className="fixed inset-y-0 left-0 z-50 w-64 flex flex-col border-r border-border-subtle bg-surface-0 overflow-y-auto md:hidden">
            {renderNavContent(false, true)}
          </aside>
        </>
      )}

      {/* ── Desktop sidebar (md+) ─────────────────────────────────────────── */}
      {/* relative z-40 + no overflow clipping: lets the branding row above overflow past the
          collapsed rail's edge and paint above the main header instead of being clipped behind it. */}
      <aside className={cn(
        'relative z-40 hidden md:flex shrink-0 border-r border-border-subtle bg-surface-0 flex-col transition-all duration-[var(--duration-slow)]',
        sidebarOpen ? 'md:w-52' : 'md:w-16',
      )}>
        {renderNavContent(!sidebarOpen, false)}
      </aside>

      {/* ── Right column: header + content + footer ───────────────────────── */}
      <div className="flex flex-1 flex-col overflow-hidden min-h-0">

        {/* Top bar */}
        <header className="flex h-12 shrink-0 items-center gap-3 border-b border-border-subtle bg-surface-0 px-4 z-30">
          {/* Hamburger — mobile only */}
          <button
            onClick={() => setMobileSidebarOpen(true)}
            className="md:hidden inline-flex h-7 w-7 items-center justify-center rounded-sm text-text-secondary hover:bg-surface-2 hover:text-text-primary transition-colors"
            aria-label="Open navigation"
          >
            <Menu className="h-4 w-4" />
          </button>

          {/* Right-side controls */}
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={togglePalette}
              className={cn(
                'group flex h-7 items-center gap-2 rounded-sm border border-border-subtle bg-surface-1 px-2 text-xs text-text-tertiary',
                'hover:bg-surface-2 hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus',
              )}
              aria-label="Global search"
            >
              <Search className="h-3 w-3" />
              <span className="hidden sm:inline">Search</span>
            </button>
            <ThemeToggle />
            <NotificationBell />
            <div ref={userMenuRef} className="relative pl-2 border-l border-border-subtle ml-1">
              <button
                onClick={() => setUserMenuOpen((o) => !o)}
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full bg-accent text-xs font-semibold uppercase text-white transition-shadow',
                  'hover:ring-2 hover:ring-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus',
                  userMenuOpen && 'ring-2 ring-accent/40',
                )}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                aria-label="User menu"
                title={displayName}
              >
                {displayName.charAt(0)}
              </button>
              {userMenuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full mt-2 w-64 rounded-md border border-border-subtle bg-surface-0 p-1 shadow-lg z-50"
                >
                  <div className="flex items-center gap-3 rounded-sm bg-surface-1 px-3 py-2.5 mb-1">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-semibold uppercase text-white">
                      {displayName.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-text-primary" title={displayName}>{displayName}</p>
                      {user?.email && (
                        <p className="truncate text-[11px] text-text-tertiary" title={user.email}>{user.email}</p>
                      )}
                    </div>
                  </div>
                  <button
                    role="menuitem"
                    onClick={handleAccountSettings}
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs text-text-secondary hover:bg-surface-1 hover:text-text-primary transition-colors"
                  >
                    <UserCog className="h-3.5 w-3.5" />
                    Account Setting
                  </button>
                  <button
                    role="menuitem"
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs text-state-danger hover:bg-state-danger/10 transition-colors"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main content area — fills all available width, no fixed max-width */}
        <main ref={mainRef} className={cn('flex-1', isCanvas ? 'overflow-hidden' : 'overflow-y-auto')}>
          {isCanvas
            ? children
            : <div className="p-4 md:p-6">{children}</div>
          }
        </main>
      </div>

      {/* Command palette — portals to document.body */}
      <CommandPalette />
    </div>
  );
}
